import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import { getAllBranch } from "../../services";
import { Branch } from "../../types/user.type";
import { SaleDepositReviewStatusFilter } from "../../types/sale-deposit.type";
import { SaleCenterClosingStatusFilter } from "../../types/sale-center-closing.type";
import DateRangePicker from "../common/DateRangePicker";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import FilterField, { FILTER_ROW_BOTTOM_SPACING } from "./FilterField";
import { DEPOSIT_STATUS_FILTER, isReviewStatusFilter } from "./Filter";
import {
  CLOSING_STATUS_FILTER,
  isClosingStatusFilter,
} from "./saleCenterClosing.utils";

interface BranchSummaryFilterProps {
  dateFrom: Dayjs;
  dateTo: Dayjs;
  onChangeDateRange: (from: Dayjs, to: Dayjs) => void;
  selectedBranch: string;
  onChangeBranch: (branchCode: string) => void;
  status: SaleDepositReviewStatusFilter;
  onChangeStatus: (status: SaleDepositReviewStatusFilter) => void;
  closingStatus: SaleCenterClosingStatusFilter;
  onChangeClosingStatus: (status: SaleCenterClosingStatusFilter) => void;
}

/**
 * The branch-day summary's filter row.
 *
 * Deliberately built from the same FilterField as the route-level filter and in the same
 * order — วันที่, สาขา, สถานะ — because the two rows sit one click apart on what the user
 * reads as one report. สายส่ง has no meaning at this altitude (a row IS every route of a
 * branch-day) and its slot is taken by สถานะการปิดยอด, the one filter that only exists here.
 *
 * The handlers take values rather than state setters: this screen keeps its filters in the
 * query string, not in component state, so returning from a drill-down restores them.
 */
export default function BranchSummaryFilter({
  dateFrom,
  dateTo,
  onChangeDateRange,
  selectedBranch,
  onChangeBranch,
  status,
  onChangeStatus,
  closingStatus,
  onChangeClosingStatus,
}: BranchSummaryFilterProps) {
  const [branches, setBranches] = useState<Branch[]>([]);

  const onGetAllBranch = useCallback(async () => {
    const {
      data: { data },
    } = await getAllBranch();
    setBranches(data ?? []);
  }, []);

  // Only a cross-branch reader ever sees this screen, so the picker is always offered.
  useEffect(() => {
    onGetAllBranch();
  }, [onGetAllBranch]);

  const handleChangeBranch = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeBranch(event.target.value);
  };

  // The options come from this file, so the guards always pass — they are what keeps the
  // select's `string` value out of the typed handlers without a cast.
  const handleChangeStatus = (event: ChangeEvent<HTMLInputElement>) => {
    if (isReviewStatusFilter(event.target.value)) {
      onChangeStatus(event.target.value);
    }
  };

  const handleChangeClosingStatus = (event: ChangeEvent<HTMLInputElement>) => {
    if (isClosingStatusFilter(event.target.value)) {
      onChangeClosingStatus(event.target.value);
    }
  };

  return (
    <Grid container spacing={2} mb={FILTER_ROW_BOTTOM_SPACING}>
      <FilterField label="วันที่">
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={onChangeDateRange}
        />
      </FilterField>
      <FilterField label="สาขา">
        <TextField
          select
          size="small"
          fullWidth
          value={selectedBranch}
          onChange={handleChangeBranch}
        >
          <MenuItem value="all">ทั้งหมด</MenuItem>
          {branches.map((branch) => (
            <MenuItem key={branch.uuid} value={branch.branch_code}>
              {branch.branch_code} - {branch.branch_name}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField label="สถานะ">
        <TextField
          select
          size="small"
          fullWidth
          value={status}
          onChange={handleChangeStatus}
        >
          {DEPOSIT_STATUS_FILTER.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField label="สถานะการปิดยอด">
        <TextField
          select
          size="small"
          fullWidth
          value={closingStatus}
          onChange={handleChangeClosingStatus}
        >
          {CLOSING_STATUS_FILTER.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
    </Grid>
  );
}
